import React from 'react';
import usePageTitle from '../hooks/usePageTitle';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';

const HOSTS = [
  {
    label: 'Playground',
    href: 'https://playground-predico.inesctec.pt/',
    host: 'playground-predico.inesctec.pt',
  },
  {
    label: 'Production',
    href: 'https://predico.inesctec.pt/',
    host: 'predico.inesctec.pt',
  },
];

export default function PlaygroundInfo() {
  usePageTitle('Playground');
  return (
    <div className="min-h-screen bg-surface dark:bg-gray-870 text-slate-900 dark:text-gray-100 flex flex-col">
      <PublicNav />

      <main className="flex-1 w-full">
        <section className="max-w-3xl mx-auto px-6 pt-12 sm:pt-16 pb-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.05] -tracking-[0.02em] text-slate-900 dark:text-white">
            <span className="block">Developers</span>
            <span className="block">
              <span className="italic font-semibold text-brand-600 dark:text-brand-400">Playground</span>.
            </span>
          </h1>
          <p className="mt-6 text-sm sm:text-base text-slate-600 dark:text-gray-300 leading-relaxed">
            A safe sandbox that mirrors operational conditions, so you can test the
            forecast submission flow end-to-end without affecting your production
            participation.
          </p>
        </section>

        <section className="max-w-3xl mx-auto px-6 pb-16 space-y-10 sm:space-y-12">
          <article>
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white mb-3">
              Introduction
            </h2>
            <p className="text-sm sm:text-base text-slate-700 dark:text-gray-300 leading-relaxed">
              Welcome to the Predico Playground — feel free to explore. This is the
              collaborative forecasting platform where forecasters share predictions
              and insights with the community.
            </p>
            <p className="mt-3 text-sm sm:text-base text-slate-700 dark:text-gray-300 leading-relaxed">
              The API endpoints exposed on this instance are identical to production —
              only the host changes:
            </p>
            <ul className="mt-3 space-y-1.5 text-sm sm:text-base">
              {HOSTS.map(({ label, href, host }) => (
                <li key={label} className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium text-slate-900 dark:text-white">{label}</span>
                  <span className="text-slate-400 dark:text-gray-600">·</span>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 dark:text-brand-400 hover:underline underline-offset-2 break-all"
                  >
                    {host}
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-sm sm:text-base text-slate-700 dark:text-gray-300 leading-relaxed">
              Browse the full OpenAPI spec via{' '}
              <a href="/swagger/" className="text-brand-600 dark:text-brand-400 hover:underline underline-offset-2">Swagger</a>{' '}
              or{' '}
              <a href="/redoc/" className="text-brand-600 dark:text-brand-400 hover:underline underline-offset-2">Redoc</a>,
              or read the{' '}
              <a
                href="https://predico.inesctec.pt/docs/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 dark:text-brand-400 hover:underline underline-offset-2"
              >
                official documentation
              </a>.
            </p>
          </article>

          <article className="border-l border-amber-400/70 dark:border-amber-500/50 pl-5 sm:pl-6">
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white mb-3">
              Heads-up
            </h2>
            <ul className="space-y-2 text-sm sm:text-base text-slate-700 dark:text-gray-300 leading-relaxed">
              <li>
                This is a <em>test environment</em>, fully decoupled from production. Submissions here do not affect your operational participation.
              </li>
              <li>
                <strong className="font-semibold text-slate-900 dark:text-white">No prizes are distributed</strong> in the playground environment.
              </li>
            </ul>
          </article>

          <article>
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white mb-3">
              Why a playground?
            </h2>
            <p className="text-sm sm:text-base text-slate-700 dark:text-gray-300 leading-relaxed">
              Use this instance to exercise the forecast submission process — wire up
              your client, validate payloads, and shake out bugs — without being
              scored on accuracy. When you're ready, switch the HOST to production
              and start your forecasting journey for real.
            </p>
            <p className="mt-3 text-sm sm:text-base text-slate-700 dark:text-gray-300 leading-relaxed">
              The environment mirrors operational conditions so your tests behave
              like the real thing:
            </p>
            <ul className="mt-2 space-y-1.5 text-sm sm:text-base text-slate-700 dark:text-gray-300 leading-relaxed list-disc list-inside marker:text-slate-400 dark:marker:text-gray-500">
              <li>Live measurement data mirrored from production.</li>
              <li>The same market sessions and challenges as production.</li>
            </ul>
          </article>

          <article>
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-white mb-3">
              Contact
            </h2>
            <p className="text-sm sm:text-base text-slate-700 dark:text-gray-300 leading-relaxed">
              For questions or support, reach out at{' '}
              <a
                href="mailto:predico@inesctec.pt"
                className="text-brand-600 dark:text-brand-400 hover:underline underline-offset-2"
              >
                predico@inesctec.pt
              </a>.
            </p>
          </article>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
